// src/App.js
import { Analytics } from "@vercel/analytics/react"
import React, { useState } from 'react';
import { useWeatherData } from './hooks/useWeatherData';
import TimeRangeSelector from './components/controls/TimeRangeSelector';
import UnitConverter from './components/controls/UnitConverter';
import DataFilter from './components/controls/DataFilter';
import TimeSeriesChart from './components/charts/TimeSeriesChart';
import CorrelationChart from './components/charts/CorrelationChart';
import StatisticsChart from './components/charts/StatisticsChart';
import CurrentConditions from './components/panels/CurrentConditions';
import Statistics from './components/panels/Statistics';
import Alerts from './components/panels/Alerts';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { RefreshCw } from 'lucide-react';

const Dashboard = () => {
  const [timeRange, setTimeRange] = useState('24h');
  const [selectedMetrics, setSelectedMetrics] = useState(['temperature', 'humidity', 'pressure']);
  const [filters, setFilters] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const {
    data,
    processedData,
    loading,
    error,
    units,
    setUnits,
    convertValue
  } = useWeatherData(timeRange);

  const handleRefresh = async () => {
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000)); // Debounce
    window.location.reload();
  };

  const filteredData = React.useMemo(() => {
    if (!filters || !data) return data;
    
    return data.filter(item => {
      return Object.entries(filters).every(([metric, bounds]) => {
        if (!bounds.min && !bounds.max) return true;
        const value = item[metric];
        if (bounds.min && value < parseFloat(bounds.min)) return false;
        if (bounds.max && value > parseFloat(bounds.max)) return false;
        return true;
      });
    });
  }, [data, filters]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center space-y-4">
          <RefreshCw className="h-12 w-12 animate-spin text-blue-500 mx-auto" />
          <p className="text-gray-600">Loading weather data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <p className="text-red-500 text-lg">{error}</p>
              <Button onClick={handleRefresh}>Try Again</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Weather Station Dashboard</span>
              <Button
                onClick={handleRefresh}
                disabled={isLoading}
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh Data
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
              <UnitConverter units={units} onUnitChange={(type, value) => setUnits(prev => ({ ...prev, [type]: value }))} />
            </div>
          </CardContent>
        </Card>

        {/* Current Conditions */}
        <CurrentConditions 
          data={data} 
          processedData={processedData} 
          units={units}
          convertValue={convertValue}
        />

        {/* Alerts */}
        <Alerts data={data} processedData={processedData} />

        {/* Analysis Section */}
        <Card>
          <CardHeader>
            <CardTitle>Data Analysis</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Metric Selector */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Select Metrics to Display:</label>
              <div className="flex flex-wrap gap-3">
                {['temperature', 'humidity', 'pressure', 'dewPoint', 'heatIndex', 'wetBulb'].map(metric => (
                  <label key={metric} className="inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedMetrics.includes(metric)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedMetrics(prev => [...prev, metric]);
                        } else {
                          setSelectedMetrics(prev => prev.filter(m => m !== metric));
                        }
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm capitalize">{metric}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Data Filter */}
            <DataFilter onFilterChange={setFilters} />

            {/* Time Series Chart */}
            <TimeSeriesChart
              data={filteredData}
              metrics={selectedMetrics}
              units={units}
              setUnits={setUnits}
              convertValue={convertValue}
              colors={['#ff7300', '#2196f3', '#4caf50', '#9c27b0', '#ff5722', '#795548']}
            />
          </CardContent>
        </Card>

        {/* Statistics */}
        <Statistics processedData={processedData} units={units} />

        {/* Correlation Analysis */}
        <Card>
          <CardHeader>
            <CardTitle>Correlation Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <CorrelationChart
                data={filteredData}
                xMetric="temperature"
                yMetric="humidity"
                units={units}
                color="#2196f3"
              />
              <CorrelationChart
                data={filteredData}
                xMetric="temperature"
                yMetric="dewPoint"
                units={units}
                color="#4caf50"
              />
            </div>
          </CardContent>
        </Card>

        {/* Distribution Analysis */}
        <Card>
          <CardHeader>
            <CardTitle>Distribution Analysis</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {selectedMetrics.map(metric => (
              <StatisticsChart
                key={metric}
                data={filteredData}
                metric={metric}
                units={units}
                color={`#${Math.floor(Math.random()*16777215).toString(16)}`}
              />
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;